/* eslint-disable @typescript-eslint/no-explicit-any */
import { format } from 'd3-format';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { LocalDateTime } from 'js-joda';
import { currencyCodeToSymbol } from '@tock/money';
import { dashboard } from 'types';
import { toInstant } from '@tock/shared/utils/time';
import protos from 'utils/protos';

const { ReportQuery } = protos;

declare type Formatter = (cents: number) => string;
type FormatterDescription = Formatter | null;

export declare type MetricType = 'number' | 'duration' | 'currency' | 'percentCents';

export interface Metric {
  desc?: string;
  formatter?: FormatterDescription;
  metric: dashboard.IMetricQuery;
  name: string;
  type: MetricType;
  negative?: boolean;
  // these are all optional display flags that could be on a metric
  // applied via utils/metrics/reporting.ts
  doubleLine?: boolean;
  hideIfZero?: boolean;
  highlight?: boolean;
  subMetric?: boolean;
}

export function filter({
  operation = 'AND',
  valueSet = [],
  stringSet = [],
  rangeSet = [],
  matchSet = [],
  filter = [],
  dateRangeSet = [],
}: {
  operation: dashboard.Filter.Operation;
  valueSet?: dashboard.ValueSet[];
  stringSet?: dashboard.StringSet[];
  rangeSet?: dashboard.IRangeSet[];
  matchSet?: dashboard.MatchSet[];
  filter?: dashboard.Filter[];
  dateRangeSet?: dashboard.DateRangeSet[];
}): dashboard.Filter {
  return {
    operation,
    valueSet,
    stringSet,
    rangeSet,
    matchSet,
    filter,
    dateRangeSet,
  };
}

export function valueSet(
  intDimension: dashboard.ReportQuery.Dimension,
  values: number[]
): dashboard.ValueSet {
  return {
    field: ReportQuery.Dimension[intDimension],
    value: values,
  };
}

export function stringSet(
  stringDimension: dashboard.ReportQuery.Dimension,
  values: string[]
): dashboard.StringSet {
  return {
    field: ReportQuery.Dimension[stringDimension],
    value: values,
  };
}

export function dateRangeSet(
  dateDimension: dashboard.ReportQuery.Dimension,
  start: LocalDateTime,
  end: LocalDateTime,
  timezone: string
): dashboard.DateRangeSet {
  return {
    field: ReportQuery.Dimension[dateDimension],
    startInstantMs: toInstant(start, timezone).toEpochMilli(),
    endInstantMs: toInstant(end, timezone).toEpochMilli(),
  };
}

export function metric(
  name: string,
  value: dashboard.MetricQuery.Metric,
  desc?: string,
  type?: MetricType,
  formatter?: FormatterDescription,
  filter?: dashboard.Filter
): Metric {
  return {
    desc,
    formatter,
    metric: {
      value,
      filter,
    },
    name,
    type: type || 'number',
  };
}

export function metricQuery(
  name: string,
  metric: dashboard.MetricQuery,
  desc?: string,
  type?: MetricType,
  formatter?: FormatterDescription,
  filter?: dashboard.Filter
): Metric {
  return {
    desc,
    formatter,
    metric,
    name,
    type: type || 'number',
  };
}

export function rename(metric: Metric, name: string, desc?: string): Metric {
  return {
    ...metric,
    name,
    desc,
  };
}

export function binaryMetric(
  name: string,
  op: dashboard.MetricQuery.Operation,
  metricA: Metric,
  metricB: Metric,
  desc?: string,
  type?: MetricType | null,
  formatter?: FormatterDescription,
  filter?: dashboard.Filter
): Metric {
  return {
    name,
    desc,
    metric: {
      operation: op,
      unary: metricA.metric,
      binary: metricB.metric,
      filter,
    },
    type: type || 'number',
    formatter,
  };
}

export function literal(n: number): Metric {
  return {
    name: 'literal',
    desc: '',
    metric: {
      operation: 'LITERAL',
      literal: n,
    },
    type: 'number',
  };
}

export function toCurrency(cents: number): string {
  return format('$,.0f')(_.toNumber((cents / 100).toFixed(2))).replace(
    '$',
    currencyCodeToSymbol((oc(window.app) as any).currency(''))
  );
}

export function toDuration(secs: number): string {
  return `${_.toInteger(secs / 3600)}:${_.toInteger(
    (secs - _.toInteger(secs / 3600) * 3600) / 60
  )}`;
}

export function toPercent(percent: number): string {
  return `${(percent / 100.0).toFixed(2)}%`;
}

export function currencyMetric(
  name: string,
  value: dashboard.MetricQuery.Metric,
  desc?: string,
  filter?: dashboard.Filter
): Metric {
  return metric(name, value, desc, 'currency', toCurrency, filter);
}

export function currencyMetricQuery(
  name: string,
  metric: any,
  desc?: string,
  filter?: dashboard.Filter
): Metric {
  return metricQuery(name, metric, desc, 'currency', toCurrency, filter);
}

export function durationMetric(
  name: string,
  value: dashboard.MetricQuery.Metric,
  desc: string,
  filter?: dashboard.Filter
): Metric {
  return metric(name, value, desc, 'duration', toDuration, filter);
}

export function percentMetric(
  name: string,
  value: dashboard.MetricQuery.Metric,
  desc: string,
  filter?: dashboard.Filter
): Metric {
  return metric(name, value, desc, 'percentCents', toPercent, filter);
}

// Helpers

/**
 * Creates a metric from a metric enum value
 */
export function m(name: dashboard.MetricQuery.Metric): Metric {
  return metric(name, name);
}

/**
 * Subtracts two metrics
 */
export function minus(m1: Metric, m2: Metric): Metric {
  return binaryMetric('anonymous', 'MINUS', m1, m2);
}

/**
 * Sums multiple metrics
 */
export function plus(m1: Metric, m2: Metric, ...ms: Metric[]): Metric {
  if (ms.length === 0) {
    return binaryMetric('anonymous', 'PLUS', m1, m2);
  } else {
    const [m3, ...moreMs] = ms;
    return binaryMetric('anonymous', 'PLUS', m1, plus(m2, m3, ...moreMs));
  }
}
